import { useState } from 'react';
import classNames from 'classnames';
import { Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isQualityMetricsEnabled } from 'shared/store/selectors/features.selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import { useUnmatchedProviders } from 'shared/hooks/quality-metric.hooks';
import { formatNumber } from 'shared/utils';
import { Button, ScreenTitle } from 'src/components/design-system';
import { NotFound } from 'src/components/common';
import { AllSettingsButton } from '../../settings.page.component';
import { QualityMetricsManagementProvider } from './quality-metrics-management.context';
import { QualityMetricsManagementLayout } from './quality-metrics-management.layout';
import { MetricFormModal } from './components/metric-form-modal';
import { UnmatchedProvidersModal } from './components/unmatched-providers-modal';
import styles from './quality-metrics-management.module.scss';

function ManagementView() {
  const lang = useSelector(getLang('QUALITY_METRICS_MANAGEMENT'));

  // `null` closes the modal, a metric opens it in edit mode, and `'new'` opens
  // it for creation — one modal serves both.
  const [formMetric, setFormMetric] = useState(null);
  const [isUnmatchedProvidersOpen, setUnmatchedProvidersOpen] = useState(false);
  // Same query as the modal, so the count and the list never disagree.
  const { data: unmatchedProviders } = useUnmatchedProviders();

  return (
    <>
      <AllSettingsButton />

      <div className={styles.header}>
        <ScreenTitle title={lang.TITLE} containerStyle={styles.title} />
        <div className={styles.headerActions}>
          <Button type="default" onClick={() => setUnmatchedProvidersOpen(true)}>
            {unmatchedProviders?.length
              ? lang.UNMATCHED_PROVIDERS_COUNT.replace(
                  '{count}',
                  formatNumber(unmatchedProviders.length),
                )
              : lang.UNMATCHED_PROVIDERS}
          </Button>
          <Button type="primary" onClick={() => setFormMetric('new')}>
            {lang.NEW_METRIC}
          </Button>
        </div>
      </div>
      <p className={styles.subtitle}>{lang.SUBTITLE}</p>

      <QualityMetricsManagementProvider>
        <QualityMetricsManagementLayout onEdit={setFormMetric} />
        {/* Inside the provider: it names each provider's metrics from the loaded list. */}
        <UnmatchedProvidersModal
          open={isUnmatchedProvidersOpen}
          onClose={() => setUnmatchedProvidersOpen(false)}
        />
      </QualityMetricsManagementProvider>

      <MetricFormModal
        open={!!formMetric}
        metric={formMetric === 'new' ? null : formMetric}
        onClose={() => setFormMetric(null)}
      />
    </>
  );
}

/**
 * Internal screen for managing the organization's quality metrics: the same
 * sidebar-and-detail shape as the clinician page, but showing the group's
 * standing instead of the viewer's, plus create and edit.
 *
 * Access is enforced twice — the route is wrapped in a `PrivateRoute` with
 * `ROOT_PERMISSIONS_GROUP`, and the backend 403s non-root callers on
 * `POST /` and `PUT /:id`.
 */
export function QualityMetricsManagementPage() {
  const isEnabled = useSelector(isQualityMetricsEnabled);

  if (!isEnabled) {
    return <NotFound />;
  }

  return (
    <div className={classNames('new-design-system', styles.root)}>
      <Routes>
        <Route index element={<ManagementView />} />
      </Routes>
    </div>
  );
}
